import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CategorySection = () => {
  const categories = [
    {
      id: 1,
      name: "Smartphones",
      description: "Latest flagship phones with cutting-edge technology",
      image: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400&h=300&fit=crop",
      icon: "Smartphone",
      productCount: 156,
      trending: true,
      gradient: "from-primary/20 to-primary/5"
    },
    {
      id: 2,
      name: "Laptops",
      description: "Professional-grade laptops for work and gaming",
      image: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400&h=300&fit=crop",
      icon: "Laptop",
      productCount: 89,
      trending: false,
      gradient: "from-secondary/20 to-secondary/5"
    },
    {
      id: 3,
      name: "Audio",
      description: "Premium headphones and wireless earbuds",
      image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=300&fit=crop",
      icon: "Headphones",
      productCount: 234,
      trending: true,
      gradient: "from-accent/20 to-accent/5"
    },
    {
      id: 4,
      name: "Accessories",
      description: "Chargers, cases, and essential tech accessories",
      image: "https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=400&h=300&fit=crop",
      icon: "Cable",
      productCount: 312,
      trending: false,
      gradient: "from-purple-500/20 to-purple-500/5"
    },
    {
      id: 5,
      name: "Smart Home",
      description: "IoT devices and smart home automation",
      image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
      icon: "Home",
      productCount: 78,
      trending: true,
      gradient: "from-emerald-500/20 to-emerald-500/5"
    },
    {
      id: 6,
      name: "Gaming",
      description: "Gaming peripherals and accessories",
      image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&h=300&fit=crop",
      icon: "Gamepad2",
      productCount: 145,
      trending: false,
      gradient: "from-red-500/20 to-red-500/5"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-background to-card/50">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-2 mb-6">
            <Icon name="Grid3X3" size={16} className="text-primary" />
            <span className="text-sm font-mono text-primary">PRODUCT CATEGORIES</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Explore Our
            </span>
            <br />
            <span className="text-foreground">Tech Universe</span>
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Discover premium electronics across multiple categories, each carefully curated for quality, 
            performance, and innovation. From smartphones to smart homes, we've got your tech needs covered.
          </p>
        </motion.div>

        {/* Categories Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {categories?.map((category) => (
            <motion.div
              key={category?.id}
              variants={itemVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group"
            >
              <Link to="/category-pages" className="block">
                <div className={`holographic-card p-6 rounded-2xl h-full relative overflow-hidden bg-gradient-to-br ${category?.gradient}`}>
                  {/* Trending Badge */}
                  {category?.trending && (
                    <div className="absolute top-4 right-4 z-10">
                      <div className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                        <Icon name="TrendingUp" size={12} />
                        <span>Trending</span>
                      </div>
                    </div>
                  )}

                  {/* Category Image */}
                  <div className="relative mb-6 overflow-hidden rounded-xl">
                    <div className="aspect-video bg-gradient-to-br from-muted/20 to-muted/40">
                      <Image
                        src={category?.image}
                        alt={category?.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                    
                    {/* Overlay Icon */}
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-primary/90 backdrop-blur-sm rounded-full p-4">
                        <Icon name={category?.icon} size={32} color="white" />
                      </div>
                    </div>
                  </div>

                  {/* Category Info */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-primary/10 border border-primary/30 rounded-lg">
                          <Icon name={category?.icon} size={20} className="text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                          {category?.name}
                        </h3>
                      </div>
                      <Icon 
                        name="ArrowRight" 
                        size={20} 
                        className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" 
                      />
                    </div>

                    <p className="text-muted-foreground leading-relaxed">
                      {category?.description}
                    </p>

                    <div className="flex items-center justify-between pt-2">
                      <div className="flex items-center space-x-2">
                        <div className="inventory-pulse high"></div>
                        <span className="text-sm text-muted-foreground">
                          {category?.productCount} products available
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-primary">
                        Explore →
                      </div>
                    </div>
                  </div>

                  {/* Hover Effect Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Categories CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-16"
        >
          <Link to="/category-pages">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-primary to-secondary text-white px-8 py-4 rounded-full font-semibold text-lg trust-seal"
            >
              <span>View All Categories</span>
              <Icon name="ArrowRight" size={20} />
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default CategorySection;